import { useState, useEffect, useCallback, useRef } from 'react'
import { searchApi } from '../api/searchApi'
import type { SearchResponse } from '../types'

interface UseSearchReturn {
  results: SearchResponse['hits']
  total: number
  page: number
  totalPages: number
  loading: boolean
  error: string | null
  query: string
  setQuery: (q: string) => void
  setPage: (page: number) => void
}

export function useSearch(initialQuery = '', initialPage = 1): UseSearchReturn {
  const [query, setQueryState] = useState(initialQuery)
  const [page, setPageState] = useState(initialPage)
  const [response, setResponse] = useState<SearchResponse>({
    hits: [],
    total: 0,
    page: 1,
    totalPages: 1,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doSearch = useCallback((q: string, p: number) => {
    if (!q.trim()) {
      setResponse({ hits: [], total: 0, page: 1, totalPages: 1 })
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    searchApi
      .search({ q, page: p, pageSize: 10 })
      .then((data) => {
        setResponse(data)
        setLoading(false)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Search failed')
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      doSearch(query, page)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, page, doSearch])

  const setQuery = useCallback((q: string) => {
    setQueryState(q)
    setPageState(1)
  }, [])

  const setPage = useCallback((p: number) => {
    setPageState(p)
  }, [])

  return {
    results: response.hits,
    total: response.total,
    page: response.page,
    totalPages: response.totalPages,
    loading,
    error,
    query,
    setQuery,
    setPage,
  }
}
